import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../Layout/GRISidebar";
import styles from "../../../styles/gri/general.module.css";
import { Box, Button, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import CustomTextField from "../GRI300/GRI308/CustomTextField";
import SectionHeading from "../GRI300/GRI308/SectionHeading";
import ReviewBox from "../../../Components/Common/ReviewBox";
import { currentYear } from "../../../utils/generateYearRanges";

const Economic225 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    remediationCommitments: "",
    grievanceMechanisms: "",
    remediationProcesses: "",
    stakeholderInvolvementGrievance: "",
    grievanceMechanismEffectiveness: "",
  });
  const [griReview, setGriReview] = useState(() =>
    JSON.parse(localStorage.getItem("griReview"))
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(
          `/gri/get?type=GeneralDisclosures2021&Id=${id}`
        );
        if (response?.data?.data?.year < currentYear - 1) setDisabled(true);
        const data =
          response.data.data.GeneralDisclosures2021?.processToRemediate || {};

        setFormData({
          remediationCommitments: data.remediationCommitments || "",
          grievanceMechanisms: data.grievanceMechanisms || "",
          remediationProcesses: data.remediationProcesses || "",
          stakeholderInvolvementGrievance:
            data.stakeholderInvolvementGrievance || "",
          grievanceMechanismEffectiveness:
            data.grievanceMechanismEffectiveness || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    if (!formData.remediationCommitments) {
      newErrors.remediationCommitments = "This field is required";
      formIsValid = false;
    }

    if (!formData.grievanceMechanisms) {
      newErrors.grievanceMechanisms = "This field is required";
      formIsValid = false;
    }

    if (!formData.remediationProcesses) {
      newErrors.remediationProcesses = "This field is required";
      formIsValid = false;
    }

    if (!formData.stakeholderInvolvementGrievance) {
      newErrors.stakeholderInvolvementGrievance = "This field is required";
      formIsValid = false;
    }

    if (!formData.grievanceMechanismEffectiveness) {
      newErrors.grievanceMechanismEffectiveness = "This field is required";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      // if (!validateForm()) return;
      const payload = {
        type: "GeneralDisclosures2021",
        processToRemediate: {
          ...formData,
        },
        submitted2_25: true,
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/general-disclosure/2-26/${id}`);
      } catch (error) {
        console.error("Error updating data:", error);
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/general-disclosure/2-26/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {/* <SectionHeading text="2-1 Activities, value chain and other business relationships" /> */}
      <p className={styles.main_heading}>
        2-25 Processes to remediate negative impacts
      </p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section A */}
        <SectionHeading
          text="a. Describe organization's commitments to provide for or cooperate in the remediation of negative impacts that the organization identifies it has caused or contributed to. Please provide with description."
          sx={{ marginTop: hide ? "-1rem" : "0rem" }}
        />
        <CustomTextField
          value={formData.remediationCommitments}
          onChange={(e) =>
            handleInputChange("remediationCommitments", e.target.value)
          }
          error={errors.remediationCommitments}
          helperText={errors.remediationCommitments}
          disabled={disabled}
        />

        <SectionHeading text="b. Describe organization's approach to identify and address grievances, including the grievance mechanisms that the organization has established or participates in. These could include internal hotlines, complaint procedures, or participation in third-party grievance mechanisms set up by industry associations or government bodies." />
        <CustomTextField
          value={formData.grievanceMechanisms}
          onChange={(e) =>
            handleInputChange("grievanceMechanisms", e.target.value)
          }
          error={errors.grievanceMechanisms}
          helperText={errors.grievanceMechanisms}
          disabled={disabled}
        />

        <SectionHeading text="c. Describe other processes by which the organization provides for or cooperates in the remediation of negative impacts that it identifies it has caused or contributed to. This might involve community engagement programs or dialogue processes to address broader social or environmental issues." />
        <CustomTextField
          value={formData.remediationProcesses}
          onChange={(e) =>
            handleInputChange("remediationProcesses", e.target.value)
          }
          error={errors.remediationProcesses}
          helperText={errors.remediationProcesses}
          disabled={disabled}
        />

        <SectionHeading text="d. Describe how the stakeholders who are the intended users of the grievance mechanisms are involved in the design, review, operation, and improvement of these mechanisms. This could involve consultations, feedback loops, or representation on grievance committees." />
        <CustomTextField
          value={formData.stakeholderInvolvementGrievance}
          onChange={(e) =>
            handleInputChange("stakeholderInvolvementGrievance", e.target.value)
          }
          error={errors.stakeholderInvolvementGrievance}
          helperText={errors.stakeholderInvolvementGrievance}
          disabled={disabled}
        />

        <SectionHeading text="e. Describe how the organization tracks the effectiveness of the grievance mechanisms and other remediation processes, and report examples of their effectiveness, including stakeholder feedback." />
        <CustomTextField
          value={formData.grievanceMechanismEffectiveness}
          onChange={(e) =>
            handleInputChange("grievanceMechanismEffectiveness", e.target.value)
          }
          error={errors.grievanceMechanismEffectiveness}
          helperText={errors.grievanceMechanismEffectiveness}
          disabled={disabled}
        />

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default Economic225;
